/**
 * Seed data for the Exemplu SRL demo — poultry distributor in Bihor/Oradea.
 *
 * `seedIfEmpty()` runs on first load (when `clienti` is empty).
 * `resetDemo()` wipes every table and reseeds (the "Resetează demonstrația" button).
 *
 * Dynamic rows (orders, messages, activity, last-order dates) are computed
 * relative to the real "today" so the demo always looks current.
 */

import { STATUS_ORDER } from '@/lib/format'
import {
  type Activitate,
  type Client,
  type Comanda,
  type ComandaLinie,
  type ComandaStatus,
  type Factura,
  type Masina,
  type Mesaj,
  type Produs,
  type Setari,
  type Sofer,
  db,
} from './dexie'

// The one constant to swap for the real business name/identity.
export const COMPANIE = {
  companie: 'Exemplu SRL',
  cui: 'RO 12345678',
  tagline: 'De la mesaj la livrare.',
  anInfiintare: '1991',
  adresa: 'Str. Depozitelor 12, Oradea, jud. Bihor',
  telefon: '0259 123 456',
  iban: 'RO49 BTRL 0130 1205 9384 00XX',
}

// --- helpers ---------------------------------------------------------------
const round2 = (n: number) => Math.round(n * 100) / 100
const round1 = (n: number) => Math.round(n * 10) / 10

function dt(dayOffset: number, hour: number, min = 0): string {
  const d = new Date()
  d.setDate(d.getDate() + dayOffset)
  d.setHours(hour, min, 0, 0)
  return d.toISOString()
}

function zi(dayOffset: number): string {
  const d = new Date()
  d.setDate(d.getDate() + dayOffset)
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${m}-${day}`
}

// --- static tables ---------------------------------------------------------
export const PRODUSE: Produs[] = [
  {
    id: 'P01',
    cod: 'P01',
    nume: 'Piept dezosat',
    categorie: 'Pui',
    pretKg: 26.5,
    stocKg: 320,
    abator: 'Avicola Oradea',
    um: 'kg',
  },
  {
    id: 'P02',
    cod: 'P02',
    nume: 'Piept cu os',
    categorie: 'Pui',
    pretKg: 18.9,
    stocKg: 210,
    abator: 'Avicola Oradea',
    um: 'kg',
  },
  {
    id: 'P03',
    cod: 'P03',
    nume: 'Pulpe superioare',
    categorie: 'Pui',
    pretKg: 15.5,
    stocKg: 280,
    abator: 'Avicola Oradea',
    um: 'kg',
  },
  {
    id: 'P04',
    cod: 'P04',
    nume: 'Pulpe inferioare',
    categorie: 'Pui',
    pretKg: 13.9,
    stocKg: 190,
    abator: 'Avicola Salonta',
    um: 'kg',
  },
  {
    id: 'P05',
    cod: 'P05',
    nume: 'Aripioare',
    categorie: 'Pui',
    pretKg: 16.9,
    stocKg: 150,
    abator: 'Avicola Salonta',
    um: 'kg',
  },
  {
    id: 'P06',
    cod: 'P06',
    nume: 'Tacâmuri de pui',
    categorie: 'Pui',
    pretKg: 8.5,
    stocKg: 240,
    abator: 'Avicola Oradea',
    um: 'kg',
  },
  {
    id: 'P07',
    cod: 'P07',
    nume: 'Pui întreg',
    categorie: 'Pui',
    pretKg: 12.5,
    stocKg: 300,
    abator: 'Avicola Oradea',
    um: 'kg',
  },
  {
    id: 'P08',
    cod: 'P08',
    nume: 'Ficăței de pui',
    categorie: 'Organe',
    pretKg: 11.5,
    stocKg: 90,
    abator: 'Avicola Salonta',
    um: 'kg',
  },
  {
    id: 'P09',
    cod: 'P09',
    nume: 'Pipote',
    categorie: 'Organe',
    pretKg: 9.9,
    stocKg: 70,
    abator: 'Avicola Salonta',
    um: 'kg',
  },
  {
    id: 'P10',
    cod: 'P10',
    nume: 'Gât de pui',
    categorie: 'Pui',
    pretKg: 7.5,
    stocKg: 60,
    abator: 'Avicola Oradea',
    um: 'kg',
  },
  {
    id: 'P11',
    cod: 'P11',
    nume: 'Carcasă pui',
    categorie: 'Pui',
    pretKg: 6.9,
    stocKg: 110,
    abator: 'Avicola Oradea',
    um: 'kg',
  },
  {
    id: 'P12',
    cod: 'P12',
    nume: 'Șnițel de pui',
    categorie: 'Procesate',
    pretKg: 29.9,
    stocKg: 130,
    abator: 'Exemplu SRL',
    um: 'kg',
  },
  {
    id: 'P13',
    cod: 'P13',
    nume: 'Pulpe dezosate',
    categorie: 'Pui',
    pretKg: 22.5,
    stocKg: 140,
    abator: 'Avicola Oradea',
    um: 'kg',
  },
  {
    id: 'P14',
    cod: 'P14',
    nume: 'Spinări / tacâmuri supă',
    categorie: 'Pui',
    pretKg: 5.9,
    stocKg: 80,
    abator: 'Avicola Salonta',
    um: 'kg',
  },
]

const P = new Map(PRODUSE.map((p) => [p.id, p]))

export const SOFERI: Sofer[] = [
  {
    id: 's1',
    nume: 'Nelu Bota',
    telefon: '0741 118 224',
    permis: 'C, CE',
    masinaId: 'm2',
    status: 'pe traseu',
  },
  {
    id: 's2',
    nume: 'Florin Chiș',
    telefon: '0742 337 561',
    permis: 'C',
    masinaId: 'm1',
    status: 'disponibil',
  },
  {
    id: 's3',
    nume: 'Adrian Moga',
    telefon: '0743 902 118',
    permis: 'C, CE',
    masinaId: 'm3',
    status: 'disponibil',
  },
  { id: 's4', nume: 'Vasile Lung', telefon: '0744 615 073', permis: 'C', status: 'liber' },
]

export const MASINI: Masina[] = [
  {
    id: 'm1',
    nrInmatriculare: 'BH 12 EXP',
    capacitateKg: 1500,
    frigorific: true,
    status: 'disponibilă',
    kmAzi: 0,
  },
  {
    id: 'm2',
    nrInmatriculare: 'BH 07 POU',
    capacitateKg: 1200,
    frigorific: true,
    status: 'pe traseu',
    kmAzi: 78,
  },
  {
    id: 'm3',
    nrInmatriculare: 'BH 45 AVI',
    capacitateKg: 2000,
    frigorific: true,
    status: 'disponibilă',
    kmAzi: 0,
  },
  {
    id: 'm4',
    nrInmatriculare: 'BH 88 FRG',
    capacitateKg: 900,
    frigorific: true,
    status: 'service',
    kmAzi: 0,
  },
]

// --- dynamic builders ------------------------------------------------------
function buildClienti(): Client[] {
  return [
    {
      id: 'c1',
      nume: 'Măcelăria Crișul',
      tip: 'carmangerie',
      localitate: 'Oradea',
      telefon: '0721 445 190',
      adresa: 'Str. Republicii 24, Oradea',
      comandaUzuala: [
        { produsId: 'P01', cantitateKg: 40 },
        { produsId: 'P03', cantitateKg: 30 },
        { produsId: 'P05', cantitateKg: 20 },
        { produsId: 'P08', cantitateKg: 10 },
      ],
      pretSpecial: { P01: 25.5 },
      soldRestant: 0,
      notite: 'Preferă livrarea dimineața, înainte de ora 9.',
      dataUltimaComanda: zi(-1),
    },
    {
      id: 'c2',
      nume: 'Minimarket Dacia',
      tip: 'minimarket',
      localitate: 'Oradea',
      telefon: '0745 210 668',
      adresa: 'Bd. Dacia 112, Oradea',
      comandaUzuala: [
        { produsId: 'P07', cantitateKg: 25 },
        { produsId: 'P06', cantitateKg: 30 },
        { produsId: 'P02', cantitateKg: 20 },
      ],
      pretSpecial: {},
      soldRestant: 840,
      notite: 'Factură pe firmă, plată la 14 zile.',
      dataUltimaComanda: zi(-3),
    },
    {
      id: 'c3',
      nume: 'Magazin Familia Pop',
      tip: 'magazin',
      localitate: 'Marghita',
      telefon: '0722 771 034',
      adresa: 'Str. Republicii 8, Marghita',
      comandaUzuala: [
        { produsId: 'P07', cantitateKg: 20 },
        { produsId: 'P03', cantitateKg: 15 },
        { produsId: 'P11', cantitateKg: 10 },
      ],
      pretSpecial: { P07: 12.0 },
      soldRestant: 0,
      notite: '',
      dataUltimaComanda: zi(-2),
    },
    {
      id: 'c4',
      nume: 'Restaurant La Ovidiu',
      tip: 'restaurant',
      localitate: 'Oradea',
      telefon: '0731 508 442',
      adresa: 'Str. Aurel Lazăr 5, Oradea',
      comandaUzuala: [
        { produsId: 'P01', cantitateKg: 30 },
        { produsId: 'P12', cantitateKg: 15 },
        { produsId: 'P05', cantitateKg: 15 },
      ],
      pretSpecial: { P01: 25.9 },
      soldRestant: 0,
      notite: 'Cere piept calibrat; comandă des șnițel.',
      dataUltimaComanda: zi(-2),
    },
    {
      id: 'c5',
      nume: 'Carmangeria Bihoreana',
      tip: 'carmangerie',
      localitate: 'Salonta',
      telefon: '0740 663 271',
      adresa: 'Str. Republicii 40, Salonta',
      comandaUzuala: [
        { produsId: 'P02', cantitateKg: 40 },
        { produsId: 'P04', cantitateKg: 30 },
        { produsId: 'P06', cantitateKg: 25 },
        { produsId: 'P14', cantitateKg: 20 },
      ],
      pretSpecial: {},
      soldRestant: 1250.5,
      notite: 'Sold restant mai vechi — de urmărit la următoarea livrare.',
      dataUltimaComanda: zi(-3),
    },
    {
      id: 'c6',
      nume: 'Alimentara Central',
      tip: 'magazin',
      localitate: 'Beiuș',
      telefon: '0753 118 907',
      adresa: 'Piața Moise Nicoară 2, Beiuș',
      comandaUzuala: [
        { produsId: 'P07', cantitateKg: 18 },
        { produsId: 'P03', cantitateKg: 14 },
        { produsId: 'P05', cantitateKg: 10 },
      ],
      pretSpecial: {},
      soldRestant: 0,
      notite: '',
      dataUltimaComanda: zi(-1),
    },
    {
      id: 'c7',
      nume: 'Pensiunea Codru',
      tip: 'restaurant',
      localitate: 'Aleșd',
      telefon: '0744 302 815',
      adresa: 'Str. Bobâlna 15, Aleșd',
      comandaUzuala: [
        { produsId: 'P01', cantitateKg: 20 },
        { produsId: 'P13', cantitateKg: 15 },
        { produsId: 'P08', cantitateKg: 8 },
      ],
      pretSpecial: {},
      soldRestant: 0,
      notite: 'Livrare joia.',
      dataUltimaComanda: zi(-4),
    },
    {
      id: 'c8',
      nume: 'Minimarket Steaua',
      tip: 'minimarket',
      localitate: 'Ștei',
      telefon: '0768 224 190',
      adresa: 'Str. Cuza Vodă 7, Ștei',
      comandaUzuala: [
        { produsId: 'P07', cantitateKg: 15 },
        { produsId: 'P06', cantitateKg: 20 },
        { produsId: 'P02', cantitateKg: 12 },
      ],
      pretSpecial: {},
      soldRestant: 0,
      notite: '',
      dataUltimaComanda: zi(-2),
    },
    {
      id: 'c9',
      nume: 'ABC Market',
      tip: 'supermarket',
      localitate: 'Oradea',
      telefon: '0733 771 500',
      adresa: 'Calea Aradului 80, Oradea',
      comandaUzuala: [
        { produsId: 'P01', cantitateKg: 60 },
        { produsId: 'P03', cantitateKg: 40 },
        { produsId: 'P07', cantitateKg: 40 },
        { produsId: 'P05', cantitateKg: 25 },
        { produsId: 'P02', cantitateKg: 30 },
      ],
      pretSpecial: { P01: 25.0, P03: 15.0 },
      soldRestant: 0,
      notite: 'Volum mare, livrare cu paletul; contract lunar.',
      dataUltimaComanda: zi(-1),
    },
    {
      id: 'c10',
      nume: 'Măcelăria Tradiția',
      tip: 'carmangerie',
      localitate: 'Marghita',
      telefon: '0725 640 118',
      adresa: 'Str. Horea 11, Marghita',
      comandaUzuala: [
        { produsId: 'P02', cantitateKg: 30 },
        { produsId: 'P04', cantitateKg: 25 },
        { produsId: 'P10', cantitateKg: 12 },
        { produsId: 'P09', cantitateKg: 8 },
      ],
      pretSpecial: {},
      soldRestant: 0,
      notite: '',
      dataUltimaComanda: zi(-1),
    },
  ]
}

function linie(produsId: string, cantitateKg: number, pretKg?: number): ComandaLinie {
  const p = P.get(produsId)
  if (!p) throw new Error(`Produs necunoscut: ${produsId}`)
  return {
    produsId,
    cod: p.cod,
    nume: p.nume,
    cantitateKg,
    pretKg: pretKg ?? p.pretKg,
    um: p.um,
  }
}

function totals(linii: ComandaLinie[]): { totalKg: number; totalRON: number } {
  const totalKg = round1(linii.reduce((s, l) => s + l.cantitateKg, 0))
  const totalRON = round2(linii.reduce((s, l) => s + l.cantitateKg * l.pretKg, 0))
  return { totalKg, totalRON }
}

function timelineUpTo(status: ComandaStatus, baseIso: string) {
  const idx = STATUS_ORDER.indexOf(status)
  const base = new Date(baseIso).getTime()
  const out: { status: ComandaStatus; ora: string }[] = []
  for (let i = 0; i <= idx; i++) {
    out.push({
      status: STATUS_ORDER[i] as ComandaStatus,
      ora: new Date(base + i * 42 * 60000).toISOString(),
    })
  }
  return out
}

interface ComandaSeed {
  id: string
  clientId: string
  status: ComandaStatus
  livrareOffset: number
  comandaOffset: number
  comandaHour: number
  linii: ComandaLinie[]
  soferId?: string
  masinaId?: string
  codUIT?: string
  facturaId?: string
  mesajOriginal?: string
}

function buildComenzi(clienti: Client[]): Comanda[] {
  const adresa = (id: string) => clienti.find((c) => c.id === id)?.adresa ?? ''

  const seeds: ComandaSeed[] = [
    // --- Nou -------------------------------------------------------------
    {
      id: 'cmd-1001',
      clientId: 'c3',
      status: 'Nou',
      comandaOffset: 0,
      comandaHour: 8,
      livrareOffset: 1,
      linii: [linie('P07', 20), linie('P03', 15), linie('P11', 10)],
    },
    {
      id: 'cmd-1002',
      clientId: 'c8',
      status: 'Nou',
      comandaOffset: 0,
      comandaHour: 8,
      livrareOffset: 1,
      linii: [linie('P07', 15), linie('P06', 20), linie('P02', 12)],
      mesajOriginal: 'Buna, 15 pui intreg, 20 kg tacamuri si 12 kg piept cu os. Multam',
    },
    {
      id: 'cmd-1003',
      clientId: 'c6',
      status: 'Nou',
      comandaOffset: 0,
      comandaHour: 9,
      livrareOffset: 2,
      linii: [linie('P03', 18), linie('P01', 10)],
    },
    {
      id: 'cmd-1004',
      clientId: 'c10',
      status: 'Nou',
      comandaOffset: 0,
      comandaHour: 9,
      livrareOffset: 1,
      linii: [linie('P02', 30), linie('P04', 25), linie('P10', 12), linie('P09', 8)],
    },
    // --- Confirmat -------------------------------------------------------
    {
      id: 'cmd-1005',
      clientId: 'c7',
      status: 'Confirmat',
      comandaOffset: 0,
      comandaHour: 7,
      livrareOffset: 1,
      linii: [linie('P01', 20), linie('P13', 15), linie('P08', 8)],
    },
    {
      id: 'cmd-1006',
      clientId: 'c4',
      status: 'Confirmat',
      comandaOffset: 0,
      comandaHour: 8,
      livrareOffset: 1,
      linii: [linie('P01', 30, 25.9), linie('P12', 15), linie('P05', 15)],
    },
    {
      id: 'cmd-1007',
      clientId: 'c2',
      status: 'Confirmat',
      comandaOffset: -1,
      comandaHour: 16,
      livrareOffset: 2,
      linii: [linie('P07', 25), linie('P06', 30), linie('P02', 20)],
    },
    // --- În pregătire ----------------------------------------------------
    {
      id: 'cmd-1008',
      clientId: 'c1',
      status: 'În pregătire',
      comandaOffset: -1,
      comandaHour: 17,
      livrareOffset: 0,
      linii: [linie('P01', 40, 25.5), linie('P03', 30), linie('P05', 20), linie('P08', 10)],
    },
    {
      id: 'cmd-1009',
      clientId: 'c9',
      status: 'În pregătire',
      comandaOffset: -1,
      comandaHour: 15,
      livrareOffset: 0,
      linii: [
        linie('P01', 60, 25.0),
        linie('P03', 40, 15.0),
        linie('P07', 40),
        linie('P05', 25),
        linie('P02', 30),
      ],
    },
    {
      id: 'cmd-1010',
      clientId: 'c5',
      status: 'În pregătire',
      comandaOffset: -1,
      comandaHour: 16,
      livrareOffset: 0,
      linii: [linie('P02', 40), linie('P04', 30), linie('P06', 25), linie('P14', 20)],
    },
    // --- Pe drum (route for șofer Nelu Bota / BH 07 POU) -----------------
    {
      id: 'cmd-1011',
      clientId: 'c4',
      status: 'Pe drum',
      comandaOffset: 0,
      comandaHour: 6,
      livrareOffset: 0,
      soferId: 's1',
      masinaId: 'm2',
      codUIT: '2RO2026071100341',
      linii: [linie('P01', 30, 25.9), linie('P12', 15)],
    },
    {
      id: 'cmd-1012',
      clientId: 'c6',
      status: 'Pe drum',
      comandaOffset: 0,
      comandaHour: 6,
      livrareOffset: 0,
      soferId: 's1',
      masinaId: 'm2',
      codUIT: '2RO2026071100358',
      linii: [linie('P07', 18), linie('P03', 14), linie('P05', 10)],
    },
    {
      id: 'cmd-1013',
      clientId: 'c3',
      status: 'Pe drum',
      comandaOffset: 0,
      comandaHour: 6,
      livrareOffset: 0,
      soferId: 's1',
      masinaId: 'm2',
      codUIT: '2RO2026071100372',
      linii: [linie('P07', 20), linie('P03', 15)],
    },
    // --- Livrat ----------------------------------------------------------
    {
      id: 'cmd-1014',
      clientId: 'c1',
      status: 'Livrat',
      comandaOffset: 0,
      comandaHour: 6,
      livrareOffset: 0,
      soferId: 's3',
      masinaId: 'm3',
      codUIT: '2RO2026071100290',
      facturaId: 'f4',
      linii: [linie('P01', 40, 25.5), linie('P05', 20), linie('P08', 10)],
    },
    {
      id: 'cmd-1015',
      clientId: 'c2',
      status: 'Livrat',
      comandaOffset: 0,
      comandaHour: 6,
      livrareOffset: 0,
      soferId: 's3',
      masinaId: 'm3',
      codUIT: '2RO2026071100305',
      facturaId: 'f5',
      linii: [linie('P07', 25), linie('P06', 30)],
    },
    {
      id: 'cmd-1016',
      clientId: 'c9',
      status: 'Livrat',
      comandaOffset: -1,
      comandaHour: 6,
      livrareOffset: -1,
      soferId: 's2',
      masinaId: 'm1',
      codUIT: '2RO2026071000188',
      linii: [linie('P01', 30, 25.0), linie('P02', 20)],
    },
    {
      id: 'cmd-1017',
      clientId: 'c7',
      status: 'Livrat',
      comandaOffset: -1,
      comandaHour: 6,
      livrareOffset: -1,
      soferId: 's2',
      masinaId: 'm1',
      codUIT: '2RO2026071000192',
      linii: [linie('P13', 15), linie('P08', 8)],
    },
    // --- Facturat --------------------------------------------------------
    {
      id: 'cmd-1018',
      clientId: 'c9',
      status: 'Facturat',
      comandaOffset: -2,
      comandaHour: 6,
      livrareOffset: -2,
      soferId: 's2',
      masinaId: 'm1',
      codUIT: '2RO2026070900071',
      facturaId: 'f1',
      linii: [linie('P01', 60, 25.0), linie('P03', 40, 15.0), linie('P07', 40)],
    },
    {
      id: 'cmd-1019',
      clientId: 'c4',
      status: 'Facturat',
      comandaOffset: -2,
      comandaHour: 6,
      livrareOffset: -2,
      soferId: 's3',
      masinaId: 'm3',
      codUIT: '2RO2026070900085',
      facturaId: 'f2',
      linii: [linie('P12', 20), linie('P01', 20, 25.9)],
    },
    {
      id: 'cmd-1020',
      clientId: 'c5',
      status: 'Facturat',
      comandaOffset: -3,
      comandaHour: 6,
      livrareOffset: -3,
      soferId: 's1',
      masinaId: 'm2',
      codUIT: '2RO2026070800064',
      facturaId: 'f3',
      linii: [linie('P02', 40), linie('P04', 30), linie('P06', 25)],
    },
    // --- Istoric (Livrat/Facturat, ultimele ~18 zile) — pentru grafice mai bogate.
    // Comenzi ADIȚIONALE cu ID-uri distincte; nu ating comenzile de azi/mâine.
    {
      id: 'cmd-1030',
      clientId: 'c9',
      status: 'Facturat',
      comandaOffset: -4,
      comandaHour: 6,
      livrareOffset: -4,
      soferId: 's2',
      masinaId: 'm1',
      codUIT: '2RO2026072000430',
      facturaId: 'f6',
      linii: [linie('P01', 55, 25.0), linie('P03', 35, 15.0), linie('P07', 30), linie('P05', 20)],
    },
    {
      id: 'cmd-1031',
      clientId: 'c1',
      status: 'Facturat',
      comandaOffset: -5,
      comandaHour: 6,
      livrareOffset: -5,
      soferId: 's3',
      masinaId: 'm3',
      codUIT: '2RO2026071900521',
      facturaId: 'f7',
      linii: [linie('P01', 35, 25.5), linie('P03', 25), linie('P08', 10)],
    },
    {
      id: 'cmd-1032',
      clientId: 'c5',
      status: 'Facturat',
      comandaOffset: -6,
      comandaHour: 6,
      livrareOffset: -6,
      soferId: 's1',
      masinaId: 'm2',
      codUIT: '2RO2026071800612',
      facturaId: 'f8',
      linii: [linie('P02', 38), linie('P04', 28), linie('P06', 22), linie('P14', 18)],
    },
    {
      id: 'cmd-1033',
      clientId: 'c4',
      status: 'Facturat',
      comandaOffset: -7,
      comandaHour: 6,
      livrareOffset: -7,
      soferId: 's3',
      masinaId: 'm3',
      codUIT: '2RO2026071700733',
      facturaId: 'f9',
      linii: [linie('P12', 22), linie('P01', 18, 25.9), linie('P05', 12)],
    },
    {
      id: 'cmd-1034',
      clientId: 'c2',
      status: 'Facturat',
      comandaOffset: -8,
      comandaHour: 6,
      livrareOffset: -8,
      soferId: 's2',
      masinaId: 'm1',
      codUIT: '2RO2026071600844',
      facturaId: 'f10',
      linii: [linie('P07', 28), linie('P06', 26), linie('P02', 18)],
    },
    {
      id: 'cmd-1035',
      clientId: 'c9',
      status: 'Facturat',
      comandaOffset: -9,
      comandaHour: 6,
      livrareOffset: -9,
      soferId: 's2',
      masinaId: 'm1',
      codUIT: '2RO2026071500955',
      facturaId: 'f11',
      linii: [linie('P01', 58, 25.0), linie('P03', 38, 15.0), linie('P02', 28)],
    },
    {
      id: 'cmd-1036',
      clientId: 'c10',
      status: 'Facturat',
      comandaOffset: -10,
      comandaHour: 6,
      livrareOffset: -10,
      soferId: 's1',
      masinaId: 'm2',
      codUIT: '2RO2026071401006',
      facturaId: 'f12',
      linii: [linie('P02', 32), linie('P04', 24), linie('P10', 10), linie('P09', 8)],
    },
    {
      id: 'cmd-1037',
      clientId: 'c6',
      status: 'Facturat',
      comandaOffset: -11,
      comandaHour: 6,
      livrareOffset: -11,
      soferId: 's3',
      masinaId: 'm3',
      codUIT: '2RO2026071301117',
      facturaId: 'f13',
      linii: [linie('P07', 16), linie('P03', 14), linie('P05', 9)],
    },
    {
      id: 'cmd-1038',
      clientId: 'c1',
      status: 'Facturat',
      comandaOffset: -12,
      comandaHour: 6,
      livrareOffset: -12,
      soferId: 's3',
      masinaId: 'm3',
      codUIT: '2RO2026071201208',
      facturaId: 'f14',
      linii: [linie('P01', 38, 25.5), linie('P05', 18), linie('P08', 9)],
    },
    {
      id: 'cmd-1039',
      clientId: 'c9',
      status: 'Facturat',
      comandaOffset: -13,
      comandaHour: 6,
      livrareOffset: -13,
      soferId: 's2',
      masinaId: 'm1',
      codUIT: '2RO2026071101319',
      facturaId: 'f15',
      linii: [linie('P01', 60, 25.0), linie('P03', 40, 15.0), linie('P07', 35)],
    },
    {
      id: 'cmd-1040',
      clientId: 'c3',
      status: 'Livrat',
      comandaOffset: -4,
      comandaHour: 6,
      livrareOffset: -4,
      soferId: 's1',
      masinaId: 'm2',
      codUIT: '2RO2026072000441',
      linii: [linie('P07', 20, 12.0), linie('P03', 15), linie('P11', 10)],
    },
    {
      id: 'cmd-1041',
      clientId: 'c7',
      status: 'Livrat',
      comandaOffset: -6,
      comandaHour: 6,
      livrareOffset: -6,
      soferId: 's3',
      masinaId: 'm3',
      codUIT: '2RO2026071800642',
      linii: [linie('P01', 18), linie('P13', 14), linie('P08', 8)],
    },
    {
      id: 'cmd-1042',
      clientId: 'c8',
      status: 'Livrat',
      comandaOffset: -9,
      comandaHour: 6,
      livrareOffset: -9,
      soferId: 's1',
      masinaId: 'm2',
      codUIT: '2RO2026071500943',
      linii: [linie('P07', 16), linie('P06', 20), linie('P02', 12)],
    },
    {
      id: 'cmd-1043',
      clientId: 'c5',
      status: 'Livrat',
      comandaOffset: -16,
      comandaHour: 6,
      livrareOffset: -16,
      soferId: 's2',
      masinaId: 'm1',
      codUIT: '2RO2026070801664',
      linii: [linie('P02', 36), linie('P04', 26), linie('P06', 20)],
    },
    {
      id: 'cmd-1044',
      clientId: 'c2',
      status: 'Livrat',
      comandaOffset: -18,
      comandaHour: 6,
      livrareOffset: -18,
      soferId: 's3',
      masinaId: 'm3',
      codUIT: '2RO2026070601885',
      linii: [linie('P07', 24), linie('P06', 22), linie('P02', 16)],
    },
  ]

  return seeds.map((s) => {
    const { totalKg, totalRON } = totals(s.linii)
    const dataComanda = dt(s.comandaOffset, s.comandaHour, 30)
    return {
      id: s.id,
      clientId: s.clientId,
      dataComanda,
      dataLivrare: zi(s.livrareOffset),
      status: s.status,
      linii: s.linii,
      totalKg,
      totalRON,
      soferId: s.soferId,
      masinaId: s.masinaId,
      adresaLivrare: adresa(s.clientId),
      mesajOriginal: s.mesajOriginal,
      codUIT: s.codUIT,
      facturaId: s.facturaId,
      timeline: timelineUpTo(s.status, dataComanda),
    }
  })
}

function clientCui(id: string): string {
  return `RO ${18_000_000 + Number(id.replace(/\D/g, ''))}`
}

function buildFacturi(clienti: Client[], comenzi: Comanda[]): Factura[] {
  const specs: {
    id: string
    numar: number
    comandaId: string
    efactura: Factura['eFacturaStatus']
  }[] = [
    { id: 'f1', numar: 1042, comandaId: 'cmd-1018', efactura: 'validat' },
    { id: 'f2', numar: 1043, comandaId: 'cmd-1019', efactura: 'validat' },
    { id: 'f3', numar: 1044, comandaId: 'cmd-1020', efactura: 'validat' },
    { id: 'f4', numar: 1045, comandaId: 'cmd-1014', efactura: 'trimis' },
    { id: 'f5', numar: 1046, comandaId: 'cmd-1015', efactura: 'validat' },
    // istoric — facturi pentru comenzile Facturat adăugate (secvența EXS continuă)
    { id: 'f6', numar: 1047, comandaId: 'cmd-1030', efactura: 'validat' },
    { id: 'f7', numar: 1048, comandaId: 'cmd-1031', efactura: 'validat' },
    { id: 'f8', numar: 1049, comandaId: 'cmd-1032', efactura: 'validat' },
    { id: 'f9', numar: 1050, comandaId: 'cmd-1033', efactura: 'validat' },
    { id: 'f10', numar: 1051, comandaId: 'cmd-1034', efactura: 'validat' },
    { id: 'f11', numar: 1052, comandaId: 'cmd-1035', efactura: 'validat' },
    { id: 'f12', numar: 1053, comandaId: 'cmd-1036', efactura: 'validat' },
    { id: 'f13', numar: 1054, comandaId: 'cmd-1037', efactura: 'validat' },
    { id: 'f14', numar: 1055, comandaId: 'cmd-1038', efactura: 'validat' },
    { id: 'f15', numar: 1056, comandaId: 'cmd-1039', efactura: 'validat' },
  ]

  return specs.flatMap((spec) => {
    const cmd = comenzi.find((c) => c.id === spec.comandaId)
    if (!cmd) return []
    const client = clienti.find((c) => c.id === cmd.clientId)
    if (!client) return []
    const subtotal = round2(cmd.totalRON)
    const totalTVA = round2(subtotal * 0.09)
    return [
      {
        id: spec.id,
        comandaId: cmd.id,
        serie: 'EXS',
        numar: spec.numar,
        dataEmitere: cmd.dataLivrare,
        furnizor: { nume: COMPANIE.companie, cui: COMPANIE.cui, adresa: COMPANIE.adresa },
        client: { nume: client.nume, cui: clientCui(client.id), adresa: client.adresa },
        linii: cmd.linii.map((l) => ({
          nume: l.nume ?? '',
          cantitateKg: l.cantitateKg,
          pretKg: l.pretKg,
          um: l.um,
        })),
        tva: 0.09,
        subtotal,
        totalTVA,
        total: round2(subtotal + totalTVA),
        eFacturaStatus: spec.efactura,
      },
    ]
  })
}

function buildMesaje(): Mesaj[] {
  return [
    {
      id: 'msg-1',
      clientId: 'c1',
      telefon: '0721 445 190',
      numeAfisat: 'Măcelăria Crișul',
      text: 'Bună ziua, pt mâine: 20kg piept dezosat, 15kg pulpe superioare, 10kg aripioare, 5 tăvi ficăței. Mulțumesc!',
      ora: dt(0, 8, 12),
      status: 'nou',
    },
    {
      id: 'msg-2',
      clientId: 'c4',
      telefon: '0731 508 442',
      numeAfisat: 'Restaurant La Ovidiu',
      text: 'Salut! Îmi trebuie 25 kg snitel de pui și 10 kg aripi pt sâmbătă. Mci',
      ora: dt(0, 9, 41),
      status: 'nou',
    },
    {
      id: 'msg-3',
      clientId: 'c2',
      telefon: '0745 210 668',
      numeAfisat: 'Minimarket Dacia',
      text: 'Bună, aș dori 30 buc pui întreg și 20 kg tacâmuri. Mulțumesc.',
      ora: dt(0, 10, 15),
      status: 'nou',
    },
    {
      id: 'msg-4',
      clientId: 'c6',
      telefon: '0753 118 907',
      numeAfisat: 'Alimentara Central',
      text: '18 kg pulpe superioare, 10 kg piept',
      ora: dt(0, 10, 52),
      status: 'nou',
    },
    {
      id: 'msg-5',
      clientId: 'c7',
      telefon: '0744 302 815',
      numeAfisat: 'Pensiunea Codru',
      text: 'Comanda obișnuită vă rog, pentru mâine. Mulțumim!',
      ora: dt(0, 7, 5),
      status: 'transformat',
      comandaId: 'cmd-1005',
    },
    {
      id: 'msg-6',
      clientId: 'c5',
      telefon: '0740 663 271',
      numeAfisat: 'Carmangeria Bihoreana',
      text: 'Bună ziua, 40kg piept cu os și 30 kg copane. Când puteți livra?',
      ora: dt(-1, 15, 28),
      status: 'citit',
    },
  ]
}

function buildActivitate(): Activitate[] {
  return [
    {
      id: 'act-1',
      tip: 'mesaj',
      icon: 'MessageSquarePlus',
      text: 'Mesaj nou de la Măcelăria Crișul',
      ora: dt(0, 8, 12),
    },
    {
      id: 'act-2',
      tip: 'comanda',
      icon: 'CheckCircle2',
      text: 'Comandă confirmată — Restaurant La Ovidiu',
      ora: dt(0, 8, 20),
    },
    {
      id: 'act-3',
      tip: 'flota',
      icon: 'Truck',
      text: 'Mașină plecată pe traseu — BH 07 POU, șofer Nelu Bota',
      ora: dt(0, 6, 45),
    },
    {
      id: 'act-4',
      tip: 'livrare',
      icon: 'PackageCheck',
      text: 'Livrare finalizată — Măcelăria Crișul',
      ora: dt(0, 7, 58),
    },
    {
      id: 'act-5',
      tip: 'factura',
      icon: 'FileText',
      text: 'Factură trimisă la ANAF e-Factura — EXS 1046',
      ora: dt(0, 8, 5),
    },
    {
      id: 'act-6',
      tip: 'transport',
      icon: 'ShieldCheck',
      text: 'Cod UIT emis pentru transport carne — 2RO…0305',
      ora: dt(0, 6, 40),
    },
    {
      id: 'act-7',
      tip: 'catalog',
      icon: 'Tag',
      text: 'Preț actualizat — Piept dezosat 26,50 lei/kg',
      ora: dt(-1, 18, 30),
    },
    {
      id: 'act-8',
      tip: 'client',
      icon: 'UserPlus',
      text: 'Comandă nouă înregistrată — Magazin Familia Pop',
      ora: dt(0, 8, 2),
    },
  ]
}

function buildSetari(): Setari {
  return { id: 1, ...COMPANIE }
}

// --- seed / reset ----------------------------------------------------------
async function insertAll(): Promise<void> {
  const clienti = buildClienti()
  const comenzi = buildComenzi(clienti)
  const facturi = buildFacturi(clienti, comenzi)
  const mesaje = buildMesaje()
  const activitate = buildActivitate()

  await db.transaction('rw', db.tables, async () => {
    await db.setari.put(buildSetari())
    await db.produse.bulkPut(PRODUSE)
    await db.soferi.bulkPut(SOFERI)
    await db.masini.bulkPut(MASINI)
    await db.clienti.bulkPut(clienti)
    await db.comenzi.bulkPut(comenzi)
    await db.facturi.bulkPut(facturi)
    await db.mesaje.bulkPut(mesaje)
    await db.activitate.bulkPut(activitate)
  })
}

/** Seed only when the store is empty (first load). */
export async function seedIfEmpty(): Promise<void> {
  const count = await db.clienti.count()
  if (count > 0) return
  await insertAll()
}

/** Wipe every table and reseed — the "Resetează demonstrația" action. */
export async function resetDemo(): Promise<void> {
  await db.transaction('rw', db.tables, async () => {
    await Promise.all(db.tables.map((t) => t.clear()))
  })
  await insertAll()
}
